/**
 * PreDepartureChecksTab.js
 *
 * Settings tab for customising pre-departure checklist.
 * Shows: Required (locked) + Standard (toggleable) + Custom (editable)
 *
 * UI Pattern: Collapsible sections with toggle switches
 */

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Lock,
  Info,
  RotateCcw,
  FileUp
} from 'lucide-react';
import { usePreDepartureConfig } from '../../../../hooks/domain/operations/usePreDepartureConfig';
import { CHECK_CATEGORIES } from '../../../../config/operations/preDepartureChecksLibrary';
import AddCustomCheckModal from '../modals/AddCustomCheckModal';
import ChecklistImportWizard from '../../modules/operations/import/ChecklistImportWizard';

const PreDepartureChecksTab = () => {
  const {
    requiredChecks,
    getChecksByCategory,
    getEnabledCountByCategory,
    toggleStandardCheck,
    addCustomCheck,
    removeCustomCheck,
    resetToDefaults
  } = usePreDepartureConfig();

  const [expandedSections, setExpandedSections] = useState({
    required: false,  // Collapsed by default
    engine: true,
    navigation: true,
    safety: true,
    deck: false,
    communications: false,
    custom: true
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);

  const checksByCategory = getChecksByCategory();
  const counts = getEnabledCountByCategory();

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleToggleCheck = async (checkId, currentEnabled) => {
    await toggleStandardCheck(checkId, !currentEnabled);
  };

  const handleAddCustomCheck = async (text, category) => {
    await addCustomCheck(text, category);
    setShowAddModal(false);
  };

  const handleRemoveCustomCheck = async (checkId) => {
    if (window.confirm('Remove this custom check?')) {
      await removeCustomCheck(checkId);
    }
  };

  const handleReset = async () => {
    if (confirmReset) {
      await resetToDefaults();
      setConfirmReset(false);
    } else {
      setConfirmReset(true);
      setTimeout(() => setConfirmReset(false), 3000);
    }
  };

  const handleImportComplete = async (importedChecklist) => {
    // Add each imported item as a custom check
    if (importedChecklist?.items) {
      for (const item of importedChecklist.items) {
        await addCustomCheck(item.text, item.category || 'custom');
      }
    }
    setShowImportWizard(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Pre-Departure Checks</h3>
          <p className="text-sm text-gray-500 mt-1">
            Configure which checks appear when starting a voyage.
            {' '}{counts.total} checks active.
          </p>
        </div>
        <button
          onClick={handleReset}
          className={`px-3 py-2 text-sm font-medium rounded-lg flex items-center gap-2
            ${confirmReset
              ? 'bg-red-100 text-red-700 hover:bg-red-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
        >
          <RotateCcw className="h-4 w-4" />
          {confirmReset ? 'Confirm Reset' : 'Reset to Defaults'}
        </button>
      </div>

      {/* Regulatory Checks Section (Locked) */}
      <div className="border border-amber-200 rounded-lg bg-amber-50">
        <button
          onClick={() => toggleSection('required')}
          className="w-full px-4 py-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            {expandedSections.required ? (
              <ChevronDown className="h-5 w-5 text-amber-600" />
            ) : (
              <ChevronRight className="h-5 w-5 text-amber-600" />
            )}
            <Lock className="h-4 w-4 text-amber-600" />
            <span className="font-medium text-amber-900">Regulatory Checks</span>
          </div>
          <span className="text-sm text-amber-700">{counts.required} checks (locked)</span>
        </button>

        {expandedSections.required && (
          <div className="px-4 pb-4 space-y-2">
            <p className="text-xs text-amber-700 mb-3">
              These regulatory checks cannot be disabled.
            </p>
            {requiredChecks.map(check => (
              <div
                key={check.id}
                className="flex items-center justify-between py-2 px-3 bg-white rounded border border-amber-100"
              >
                <div className="flex items-center gap-3">
                  <Lock className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-900">{check.text}</span>
                </div>
                {check.regulation && (
                  <span className="text-xs text-gray-500">{check.regulation}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Standard Check Categories */}
      {CHECK_CATEGORIES.map(category => {
        const catData = checksByCategory[category.id];
        if (!catData) return null;

        const enabledCount = catData.checks.filter(c => c.enabled).length;
        const isExpanded = expandedSections[category.id];

        return (
          <div key={category.id} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection(category.id)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
                <span className="font-medium text-gray-900">{category.label}</span>
              </div>
              <span className="text-sm text-gray-500">
                {enabledCount}/{catData.checks.length} enabled
              </span>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 space-y-2">
                {category.description && (
                  <p className="text-xs text-gray-500 mb-3">{category.description}</p>
                )}
                {catData.checks.map(check => (
                  <div
                    key={check.id}
                    className="flex items-center justify-between py-2"
                  >
                    <span className="text-gray-700">{check.text}</span>
                    <button
                      onClick={() => handleToggleCheck(check.id, check.enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        ${check.enabled ? 'bg-cyan-600' : 'bg-gray-200'}`}
                      role="switch"
                      aria-checked={check.enabled}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${check.enabled ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Custom Checks Section */}
      <div className="border border-cyan-200 rounded-lg bg-cyan-50">
        <button
          onClick={() => toggleSection('custom')}
          className="w-full px-4 py-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            {expandedSections.custom ? (
              <ChevronDown className="h-5 w-5 text-cyan-600" />
            ) : (
              <ChevronRight className="h-5 w-5 text-cyan-600" />
            )}
            <span className="font-medium text-cyan-900">Custom Checks</span>
          </div>
          <span className="text-sm text-cyan-700">{counts.custom} checks</span>
        </button>

        {expandedSections.custom && (
          <div className="px-4 pb-4 space-y-3">
            <p className="text-xs text-cyan-700">
              Add your own vessel-specific checks.
            </p>

            {/* Existing custom checks */}
            {checksByCategory.custom?.checks.map(check => (
              <div
                key={check.id}
                className="flex items-center justify-between py-2 px-3 bg-white rounded border border-cyan-100"
              >
                <span className="text-gray-900">{check.text}</span>
                <button
                  onClick={() => handleRemoveCustomCheck(check.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Add button row */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex-1 py-2 px-4 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700
                  flex items-center justify-center gap-2 font-medium"
              >
                <Plus className="h-4 w-4" />
                Add Custom Check
              </button>
              <button
                onClick={() => setShowImportWizard(true)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-cyan-300
                  text-cyan-600 rounded-lg hover:bg-cyan-50 transition-colors"
              >
                <FileUp className="h-4 w-4" />
                Import from SMS
                <span className="text-xs bg-cyan-100 px-1.5 py-0.5 rounded">PRO</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Note about existing voyages</p>
          <p className="mt-1">
            Changes here only affect NEW voyages. Existing voyages keep their
            original checklist as a snapshot for audit purposes.
          </p>
        </div>
      </div>

      {/* Add Custom Check Modal */}
      <AddCustomCheckModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddCustomCheck}
      />

      {/* Checklist Import Wizard */}
      <ChecklistImportWizard
        isOpen={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onComplete={handleImportComplete}
        initialType="pre_departure"
      />
    </div>
  );
};

export default PreDepartureChecksTab;
